"use client";

import { useLocale } from "next-intl";

import { LOCALE_CURRENCY } from "@/lib/locale-prefix";
import { formatLocalizedCurrency } from "@/lib/localize-currency";
import rootLogger from "@/lib/logging";

const logger = rootLogger.child({ module: "components/currency-mark" });

// Supported currencies for currency localization
export const SUPPORTED_CURRENCIES = ["USD", "GBP", "EUR", "AUD"] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export const DEFAULT_CURRENCY: SupportedCurrency = "USD";

// URL locale (from next-intl routing) to currency mapping — sourced from lib/locale-prefix.ts
const URL_LOCALE_CURRENCY_MAP = LOCALE_CURRENCY as Record<
  string,
  SupportedCurrency
>;

export function formatCurrency(
  value: number,
  currency: SupportedCurrency,
  locale: string,
  compact: boolean = false
): string {
  return formatLocalizedCurrency(value, locale, {
    currency,
    compact,
    maximumFractionDigits: 0,
  });
}

// Currency mark renderer with locale-aware SSR and client hydration
export const CurrencyMark = (props: {
  children?: React.ReactNode;
  text?: string;
  markKey?: string;
  value?: { value: number; compact?: boolean };
}) => {
  const { value } = props;
  const locale = useLocale();

  // URL locale is authoritative — derive currency directly (SSR-safe)
  const currency = URL_LOCALE_CURRENCY_MAP[locale] ?? DEFAULT_CURRENCY;

  // Default to standard (non-compact) formatting
  const compact = value?.compact ?? false;

  // Error handling for invalid values
  if (!value || typeof value.value !== "number" || isNaN(value.value)) {
    logger.warn(
      {
        markKey: props.markKey,
        text: props.text,
        value: value,
      },
      "CurrencyMark received invalid value"
    );
    return null;
  }

  return <span>{formatCurrency(value.value, currency, locale, compact)}</span>;
};
